import React, { useState } from 'react';
import { Badge, Button, Form, Input, Modal, Space, Table, Typography, message } from 'antd';
import { EditOutlined, TranslationOutlined } from '@ant-design/icons';
import { productService } from '../../services/productService';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';

/** feature_schema ({group: {key: {display, unit, description, values}}}) → flat rows, one per field. */
const schemaRows = (schema) => {
    let s = schema;
    if (typeof s === 'string') {
        try { s = JSON.parse(s); } catch (_) { s = null; }
    }
    const rows = [];
    Object.values(s && typeof s === 'object' ? s : {}).forEach((group) => {
        Object.entries(group && typeof group === 'object' ? group : {}).forEach(([key, def]) => {
            if (!def || typeof def !== 'object') return;
            // enum options worth a label: non-numeric values (QoS 1–4, CPU 0.5–8 need none)
            const options = def.type === 'enum' && Array.isArray(def.values)
                ? def.values.map(String).filter((v) => !/^[\d.]+$/.test(v)) : [];
            rows.push({ key, display: def.display || key, unit: def.unit || '', description: def.description || '', options });
        });
    });
    return rows;
};

/** Spec labels of one language: field × (label, unit, description, option labels). */
const SchemaLabels = ({ rows, lang }) => {
    const { t } = useTranslation();
    if (!rows.length) return null;
    const p = (key, ...rest) => ['i18n', lang, 'feature_schema', key, ...rest];
    return (
        <Form.Item label={t('Spec labels')} style={{ marginBottom: 0 }}>
            <Table size="small" rowKey="key" pagination={false} dataSource={rows} scroll={{ y: 320 }} columns={[
                {
                    title: t('Name'), dataIndex: 'display', width: 150,
                    render: (v, r) => <Typography.Text type="secondary" title={r.key}>{v}</Typography.Text>,
                },
                {
                    title: t('Label'), key: 'display_t',
                    render: (_, r) => (
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Form.Item noStyle name={p(r.key, 'display')}><Input size="small" placeholder={r.display} /></Form.Item>
                            {r.options.map((o) => (
                                <Form.Item key={o} noStyle name={p(r.key, 'options', o)}>
                                    <Input size="small" addonBefore={o} />
                                </Form.Item>
                            ))}
                        </Space>
                    ),
                },
                {
                    title: t('Unit'), key: 'unit_t', width: 100,
                    render: (_, r) => (r.unit ? <Form.Item noStyle name={p(r.key, 'unit')}><Input size="small" placeholder={r.unit} /></Form.Item> : null),
                },
                {
                    title: t('Description'), key: 'desc_t',
                    render: (_, r) => (r.description ? <Form.Item noStyle name={p(r.key, 'description')}><Input size="small" placeholder={r.description} /></Form.Item> : null),
                },
            ]} />
        </Form.Item>
    );
};

/** Product line names / descriptions / spec labels shown to customers, per language (product_types.i18n). */
export default function ProductTypeNames({ types, onSaved }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const rows = editing ? schemaRows(editing.feature_schema) : [];

    const edit = (r) => {
        setEditing(r);
        form.setFieldsValue({ name: r.name, description: r.description || '', i18n: parseI18n(r.i18n) });
    };
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const i18n = cleanI18n(mergeI18n(editing.i18n, v.i18n));
            const r = await productService.updateProductType({ id: editing.id, name: v.name, description: v.description || null, i18n });
            if (!r.success) throw r;
            message.success(t('Saved'));
            setEditing(null);
            onSaved && onSaved();
        } catch (e) {
            message.error(e.error || t('Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button icon={<TranslationOutlined />} onClick={() => setOpen(true)}>{t('Product line names')}</Button>
            <Modal title={t('Product line names')} open={open} onCancel={() => setOpen(false)} footer={null} width={640}>
                <Table size="small" rowKey="id" pagination={false} dataSource={types || []} columns={[
                    { title: t('Code'), dataIndex: 'code', width: 110 },
                    { title: t('Name'), dataIndex: 'name', render: (v, r) => (Object.keys(parseI18n(r.i18n)).length ? <Badge dot offset={[6, 0]}>{v}</Badge> : v) },
                    { title: '', key: 'op', width: 60, render: (_, r) => <Button type="link" size="small" icon={<EditOutlined />} onClick={() => edit(r)} /> },
                ]} />
            </Modal>
            <Modal title={editing?.code} open={!!editing} onCancel={() => setEditing(null)} onOk={save} confirmLoading={saving} destroyOnClose width={rows.length ? 900 : 520}>
                <Form form={form} layout="vertical" preserve={false}>
                    <I18nFieldTabs
                        fields={[
                            { key: 'name', label: t('Name') },
                            { key: 'description', label: t('Description'), type: 'textarea' },
                            { key: 'feature_schema', render: (lang) => <SchemaLabels rows={rows} lang={lang} /> },
                        ]}
                        base={<>
                            <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, max: 100 }]}><Input /></Form.Item>
                            <Form.Item name="description" label={t('Description')}><Input.TextArea rows={2} /></Form.Item>
                        </>}
                    />
                </Form>
            </Modal>
        </>
    );
}
